import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit,
    faTrash,
    faSave,
    faSearch,
    faRefresh,
    faBoxes,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faTimes,
    faChevronLeft,
    faChevronRight,
    faFilter,
    faCalendarAlt,
    faTimesCircle,
    faBox,
    faTag,
    faBuilding,
    faBalanceScale,
    faCalculator,
    faExclamationCircle,
    faHashtag,
    faPlus,
    faBarcode,
    faLayerGroup,
    faIndustry,
    faWeightHanging,
    faWarehouse,
    faFileExport,
    faChartLine,
    faPercent,
    faUser,
    faPalette,
    faRulerCombined,
    faBoxes as faBoxesIcon,
    faInfoCircle,
    faHistory,
    faStore,
    faExchangeAlt,
    faList,
    faListOl,
    faCubes,
    faMinus,
    faArrowsAlt,
    faExpand,
    faCompress,
    faArrowsLeftRight
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import "../styles/CustomerList.css";
import "../styles/Customer.css";

// Create translator instance
const createFabProductsListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_fab_list_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Fab Products List");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_fab_list_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed");
        }
        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        fabProductList: 'Fabric Products List',
        allFabProducts: 'My Fabric Products',
        fabricInventory: 'Fabric Inventory',
        shopName: 'Shop',
        shopCode: 'Shop Code',
        currentShop: 'Current Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search fabric products...',
        refresh: 'Refresh',
        loadingFabProducts: 'Loading fabric products...',
        noFabProductsFound: 'No fabric products found',
        fabProductDetails: 'Fabric Product Details',
        close: 'Close',
        serialNo: 'S.No',
        id: 'ID',
        productName: 'Product Name',
        productColorCode: 'Color Code',
        batchNumber: 'Batch',
        type: 'Type',
        totalYards: 'Total Yards',
        totalMeters: 'Total Meters',
        totalRolls: 'Total Rolls',
        brand: 'Brand',
        stockAlert: 'Alert',
        actions: 'Actions',
        addedBy: 'Added By',
        shopInfo: 'Shop',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        deleteConfirm: 'Are you sure you want to delete this fabric product?',
        deletedSuccess: 'Fabric product deleted successfully',
        deleteError: 'Error deleting fabric product',
        networkError: 'Network error occurred',
        productId: 'Product ID',
        batchNo: 'Batch Number',
        name: 'Name',
        notProvided: 'Not provided',
        inStock: 'In Stock',
        lowStock: 'Low Stock',
        outOfStock: 'Out of Stock',
        showing: 'Showing',
        of: 'of',
        products: 'products',
        previous: 'Previous',
        next: 'Next',
        filter: 'Filter',
        all: 'All',
        lowStockFilter: 'Low Stock',
        outOfStockFilter: 'Out of Stock',
        filterBy: 'Filter By',
        selectFilter: 'Select Filter',
        batchNumberFilter: 'Batch Number',
        colorCodeFilter: 'Color Code',
        typeFilter: 'Type',
        brandFilter: 'Brand',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        applyFilters: 'Apply Filters',
        resetFilters: 'Reset Filters',
        addedByFilter: 'Added By',
        editFabProduct: 'Edit Fabric Product',
        updateFabProduct: 'Update Fabric Product',
        updating: 'Updating...',
        fabProductUpdated: 'Fabric product updated successfully',
        updateError: 'Error updating fabric product',
        cancel: 'Cancel',
        saveChanges: 'Save Changes',
        fillDetails: 'Fill in the fabric product details below',
        nameRequired: 'Product name is required',
        typeOptions: 'Type Options',
        brandOptions: 'Brand Options',
        yardsRequired: 'Total yards is required',
        alertRequired: 'Low stock alert quantity is required',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this fabric product?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        myRecords: 'My Records Only',
        loadingUserData: 'Loading your fabric product data...',
        noUserRecords: 'No fabric products found in this shop',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        stockStatus: 'Stock Status',
        totalYardsValue: 'Total Yards',
        cotton: 'Cotton',
        silk: 'Silk',
        wool: 'Wool',
        linen: 'Linen',
        polyester: 'Polyester',
        blend: 'Blend',
        other: 'Other',
        selectType: 'Select Type',
        optional: 'Optional',
        enterProductName: 'Enter product name',
        enterColorCode: 'Enter color code',
        enterBatchNumber: 'Enter batch number',
        enterBrand: 'Enter brand',
        enterStockQuantity: 'Enter stock quantity',
        enterTotalRolls: 'Enter total rolls',
        zeroPlaceholder: '0',
        stockInfo: 'Yards and meters will be calculated automatically based on stock quantity',
        totalRollsHint: 'Number of rolls in this batch',
        unitYards: 'Yards',
        unitMeters: 'Meters',
        stockQuantity: 'Stock Quantity',
        unit: 'Unit'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = [];
        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });
        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }
        return translations;
    };

    loadFromCache();
    return { getTranslations, translateText };
};

const fabProductsListTranslator = createFabProductsListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        if (type === 'success') return faCheckCircle;
        return faExclamationTriangle;
    };

    const getTitleForType = (type) => {
        if (type === 'success') return translations.success || 'Success';
        if (type === 'error') return translations.error || 'Error';
        return translations.warning || 'Warning';
    };

    if (!toast.show) return null;

    return (
        <div className={`toast-notification ${toast.type}`}>
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">{getTitleForType(toast.type)}</div>
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content shop-selector-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-shops">
                            <div className="loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                    onClick={() => {
                                        onSelectShop(shop);
                                        onClose();
                                    }}
                                >
                                    <div className="shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="shop-item-details">
                                        <div className="shop-item-name">{shop.shop_name}</div>
                                        <div className="shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 500, label: 'Narrow', icon: faMinus },
    medium: { width: 750, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 950, label: 'Wide', icon: faPlus },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

const FabProductsList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 750, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('medium');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: ''
    });

    const toastTimerRef = useRef(null);

    // Edit form state - Updated to match new schema
    const [editFormData, setEditFormData] = useState({
        product_name: '',
        product_colorcode: '',
        batch_no: '',
        type: '',
        brand: '',
        unit: 'yards',
        stock_quantity: '',
        total_rolls: '',
        stock_alert: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Shop info state
    const [shopInfo, setShopInfo] = useState({
        shop_id: null,
        shop_name: '',
        shop_code: ''
    });

    // Get theme and language from localStorage
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#10b981';
    });

    const [translations, setTranslations] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Enhanced filter states
    const [filterType, setFilterType] = useState('all');
    const [filterBy, setFilterBy] = useState('all');
    const [batchFilter, setBatchFilter] = useState('');
    const [colorCodeFilter, setColorCodeFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [brandFilter, setBrandFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // Refs for date inputs
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    // Types for dropdowns
    const typeOptions = [
        { value: '', label: 'Select Type' },
        { value: 'cotton', label: 'Cotton' },
        { value: 'silk', label: 'Silk' },
        { value: 'wool', label: 'Wool' },
        { value: 'linen', label: 'Linen' },
        { value: 'polyester', label: 'Polyester' },
        { value: 'blend', label: 'Blend' },
        { value: 'other', label: 'Other' }
    ];

    const brandOptions = [
        { value: 'gul_ahmed', label: 'Gul Ahmed' },
        { value: 'al_karam', label: 'Al Karam' },
        { value: 'sapphire', label: 'Sapphire' },
        { value: 'chenone', label: 'ChenOne' },
        { value: 'bonanza', label: 'Bonanza' }
    ];

    // Calculate totals - Using total_yards instead of manual_total_yards
    const totalProducts = filteredProducts.length;
    const totalYards = filteredProducts.reduce((sum, product) => {
        return sum + (parseFloat(product.total_yards) || 0);
    }, 0);
    const lowStockCount = filteredProducts.filter(product => {
        const stock = parseFloat(product.total_yards) || 0;
        const alert = parseFloat(product.stock_alert) || 0;
        return stock <= alert && stock > 0;
    }).length;
    const outOfStockCount = filteredProducts.filter(product => {
        const stock = parseFloat(product.total_yards) || 0;
        return stock <= 0;
    }).length;

    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    // Set width mode
    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
            modalRef.current.style.maxWidth = '98vw';
            modalRef.current.style.maxHeight = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
            modalRef.current.style.maxWidth = '95vw';
            modalRef.current.style.maxHeight = '85vh';
        }
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        setTimeout(() => centerModal(), 10);
    }, [centerModal]);

    // Start dragging
    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .width-btn, .close-btn-modern')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('dragging');
    }, []);

    // Start resizing
    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = {
            x: e.clientX,
            y: e.clientY,
            width: rect.width,
            height: rect.height,
            left: rect.left,
            top: rect.top,
            minWidth: 450,
            minHeight: 550,
            maxWidth: window.innerWidth - 40,
            maxHeight: window.innerHeight - 40
        };
        modalRef.current.style.transition = 'none';
        modalRef.current.classList.add('resizing');
    }, []);

    // Handle drag and resize movement
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: newLeft, y: newTop });
            }
            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;
                switch (resizeType.current) {
                    case 'e': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); break;
                    case 'w': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                    case 's': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'n': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'ne': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'nw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'se': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'sw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                }
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                if (modalRef.current) {
                    modalRef.current.style.transition = '';
                    modalRef.current.classList.remove('dragging', 'resizing');
                }
            }
        };
        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('dragging-active');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('dragging-active');
        };
    }, [isDragging, isResizing]);

    // Initialize modal position when opened
    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => {
                centerModal();
                setWidthMode('medium');
                setIsModalInitialized(true);
            }, 50);
        }
        if (!showEditModal) setIsModalInitialized(false);
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    // Reset position on window resize
    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Function to show toast
    const showToast = (type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // Clean up timer on component unmount
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#10b981';
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };
                const palette = {
                    'customer-primary-color': color,
                    'customer-primary-dark': adjust(-40),
                    'customer-primary-light': adjust(40),
                    'customer-primary-ultralight': adjust(20),
                    'customer-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'customer-primary-shadow': `${color}20`
                };
                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
                document.documentElement.style.setProperty('--primary-color', color);
                document.documentElement.style.setProperty('--primary-dark', adjust(-40));
                document.documentElement.style.setProperty('--primary-light', adjust(40));
                document.documentElement.style.setProperty('--primary-ultralight', adjust(20));
                document.documentElement.style.setProperty('--primary-gradient', `linear-gradient(135deg, ${color}, ${adjust(-20)})`);
                document.documentElement.style.setProperty('--primary-shadow', `${color}20`);
                document.documentElement.style.setProperty('--row-active', adjust(10));
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };
        applyTheme(currentTheme);
        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#10b981';
            if (theme !== currentTheme) {
                setCurrentTheme(theme);
                applyTheme(theme);
            }
        };
        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);
        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // Load translations
    useEffect(() => {
        const loadTranslations = async () => {
            const trans = await fabProductsListTranslator.getTranslations(currentLanguage);
            setTranslations(trans);
        };
        loadTranslations();
    }, [currentLanguage]);

    // Listen for language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) setCurrentLanguage(lang);
        };
        handleLanguageChange();
        window.addEventListener('storage', handleLanguageChange);
        const interval = setInterval(handleLanguageChange, 1000);
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // Apply RTL direction
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
            document.documentElement.style.direction = 'rtl';
            document.documentElement.lang = currentLanguage;
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
            document.documentElement.style.direction = 'ltr';
            document.documentElement.lang = currentLanguage;
        }
    }, [currentLanguage]);

    // Fetch products on mount and when shop changes
    useEffect(() => {
        if (user && user.username) {
            fetchFabProducts();
            fetchAvailableShops();
        } else {
            setLoading(false);
            showToast('error', 'User not authenticated. Please login again.');
        }
    }, [user, currentShop]);

    // Apply filters
    useEffect(() => {
        applyFilters();
    }, [products, searchTerm, filterType, filterBy, batchFilter, colorCodeFilter, typeFilter, brandFilter, addedByFilter, startDate, endDate]);

    // Fetch available shops for switching
    const fetchAvailableShops = async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            setAvailableShops([]);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                fetchFabProducts();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="shop-indicator-modern no-shop">
                    <div className="shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="shop-warning">{translations.noShopSelected || 'No shop selected'}</span>
                    </div>
                    <button className="btn-modern btn-switch-shop" onClick={() => setShowShopSelector(true)}>
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }
        return null;
    };

    const fetchFabProducts = async () => {
        if (!user || !user.username) {
            showToast('error', 'User information not available. Please login again.');
            setLoading(false);
            return;
        }
        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setProducts(data.data);
                resetFilters();
                if (data.shop_id) {
                    setShopInfo(prev => ({
                        ...prev,
                        shop_id: data.shop_id,
                        shop_name: data.shop_name || prev.shop_name,
                        shop_code: data.shop_code || prev.shop_code
                    }));
                }
                if (data.data.length === 0) {
                    showToast('info', translations.noUserRecords || 'No fabric products found in this shop');
                }
            } else {
                showToast('error', data.message || translations.networkError || 'Network error occurred');
            }
        } catch (error) {
            console.error('Error fetching fabric products:', error);
            showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = () => {
        let filtered = [...products];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                product.product_name?.toLowerCase().includes(term) ||
                product.product_colorcode?.toLowerCase().includes(term) ||
                product.batch_no?.toLowerCase().includes(term) ||
                product.brand?.toLowerCase().includes(term) ||
                product.type?.toLowerCase().includes(term) ||
                product.added_by_username?.toLowerCase().includes(term) ||
                product.username?.toLowerCase().includes(term)
            );
        }
        if (filterType === 'lowStock') {
            filtered = filtered.filter(product => {
                const stock = parseFloat(product.total_yards) || 0;
                const alert = parseFloat(product.stock_alert) || 0;
                return stock <= alert && stock > 0;
            });
        } else if (filterType === 'outOfStock') {
            filtered = filtered.filter(product => {
                const stock = parseFloat(product.total_yards) || 0;
                return stock <= 0;
            });
        }
        if (filterBy === 'batch' && batchFilter.trim()) {
            filtered = filtered.filter(product => product.batch_no?.toLowerCase().includes(batchFilter.toLowerCase()));
        }
        if (filterBy === 'colorCode' && colorCodeFilter.trim()) {
            filtered = filtered.filter(product => product.product_colorcode?.toLowerCase().includes(colorCodeFilter.toLowerCase()));
        }
        if (filterBy === 'type' && typeFilter.trim()) {
            filtered = filtered.filter(product => product.type?.toLowerCase() === typeFilter.toLowerCase());
        }
        if (filterBy === 'brand' && brandFilter.trim()) {
            filtered = filtered.filter(product => product.brand?.toLowerCase().includes(brandFilter.toLowerCase()));
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            filtered = filtered.filter(product =>
                (product.added_by_username?.toLowerCase().includes(addedByFilter.toLowerCase())) ||
                (product.username?.toLowerCase().includes(addedByFilter.toLowerCase()))
            );
        }
        if (startDate) {
            const start = new Date(startDate);
            filtered = filtered.filter(product => {
                if (!product.date) return false;
                const productDate = new Date(product.date);
                return productDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            filtered = filtered.filter(product => {
                if (!product.date) return false;
                const productDate = new Date(product.date);
                end.setHours(23, 59, 59, 999);
                return productDate <= end;
            });
        }
        setFilteredProducts(filtered);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilterBy('all');
        setBatchFilter('');
        setColorCodeFilter('');
        setTypeFilter('');
        setBrandFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setFilterType('all');
    };

    const handleView = (product) => {
        setSelectedProduct(product);
        setShowViewModal(true);
    };

    const handleEdit = (product) => {
        if (product.username !== user.username) {
            showToast('error', 'You can only edit your own fabric product records');
            return;
        }
        setSelectedProduct(product);
        setEditFormData({
            product_name: product.product_name || '',
            product_colorcode: product.product_colorcode || '',
            batch_no: product.batch_no || '',
            type: product.type || '',
            brand: product.brand || '',
            unit: product.unit || 'yards',
            stock_quantity: product.unit === 'yards' ? product.total_yards : product.total_meters,
            total_rolls: product.total_rolls || '',
            stock_alert: product.stock_alert || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (productId) => {
        const product = products.find(p => p.id === productId);
        if (!product || product.username !== user.username) {
            showToast('error', 'You can only delete your own fabric product records');
            return;
        }
        setProductToDelete(productId);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!productToDelete || !currentShop) return;
        const product = products.find(p => p.id === productToDelete);
        if (!product || product.username !== user.username) {
            showToast('error', 'You can only delete your own fabric product records');
            setShowDeleteModal(false);
            setProductToDelete(null);
            return;
        }
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete',
                    id: productToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Fabric product deleted successfully');
                fetchFabProducts();
                setShowDeleteModal(false);
                setProductToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting fabric product');
            }
        } catch (error) {
            console.error('Error deleting fabric product:', error);
            showToast('error', translations.deleteError || 'Error deleting fabric product');
        }
    };

    // Validate edit form
    const validateEditForm = () => {
        const newErrors = {};
        if (!editFormData.product_name.trim()) {
            newErrors.product_name = translations.nameRequired || 'Product name is required';
        }
        if (!editFormData.stock_quantity || parseFloat(editFormData.stock_quantity) <= 0) {
            newErrors.stock_quantity = translations.stockQuantityRequired || 'Stock quantity is required';
        }
        if (!editFormData.stock_alert || parseFloat(editFormData.stock_alert) < 0) {
            newErrors.stock_alert = translations.alertRequired || 'Low stock alert quantity is required';
        }
        setEditFormErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
        if (editFormErrors[name]) setEditFormErrors(prev => ({ ...prev, [name]: '' }));
    };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!selectedProduct || !currentShop) return;
    if (selectedProduct.username !== user.username) {
        showToast('error', 'You can only update your own fabric product records');
        return;
    }
    if (!validateEditForm()) return;
    
    setEditLoading(true);
    try {
        const stockQuantity = parseFloat(editFormData.stock_quantity) || 0;
        
        // ✅ FIX: Added 'mode: replace' to ensure backend does a full update
        const updateData = {
            action: 'edit',
            mode: 'replace', // <-- Tells PHP to REPLACE values, not ADD them
            id: selectedProduct.id,
            product_name: editFormData.product_name.trim(),
            product_colorcode: editFormData.product_colorcode.trim() || null,
            batch_no: editFormData.batch_no,
            type: editFormData.type || null,
            brand: editFormData.brand || null,
            unit: editFormData.unit,
            stock_quantity: stockQuantity,
            total_rolls: editFormData.total_rolls ? parseInt(editFormData.total_rolls) : 0,
            stock_alert: parseFloat(editFormData.stock_alert) || 0,
            username: user.username,
            shop_id: currentShop.shop_id,
            date: selectedProduct.date || new Date().toISOString().split('T')[0]
        };
        
        const response = await fetch('https://ourzestapp.online/oz_backend/fab_products.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updateData)
        });
        const data = await response.json();
        
        if (data.status === 'success') {
            showToast('success', translations.fabProductUpdated || 'Fabric product updated successfully');
            fetchFabProducts();
            setShowEditModal(false);
            setSelectedProduct(null);
            setEditFormErrors({});
        } else {
            showToast('error', data.message || translations.updateError || 'Error updating fabric product');
        }
    } catch (error) {
        console.error('Error updating fabric product:', error);
        showToast('error', translations.updateError || 'Error updating fabric product');
    } finally {
        setEditLoading(false);
    }
};

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        } catch (error) {
            return dateString;
        }
    };

    // Format number
    const formatNumber = (num, decimals = 2) => {
        if (num === undefined || num === null || num === '') return '0.00';
        return parseFloat(num).toFixed(decimals);
    };

    // Get stock status
    const getStockStatus = (stock, alert) => {
        const qty = parseFloat(stock) || 0;
        const alertQty = parseFloat(alert) || 0;
        if (qty <= 0) {
            return { status: 'out-of-stock', text: translations.outOfStock || 'Out of Stock', color: '#ef4444' };
        } else if (qty <= alertQty) {
            return { status: 'low-stock', text: translations.lowStock || 'Low Stock', color: '#f59e0b' };
        } else {
            return { status: 'in-stock', text: translations.inStock || 'In Stock', color: '#10b981' };
        }
    };

    // Get today's date in YYYY-MM-DD format for date inputs
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Set max date for end date based on start date
    useEffect(() => {
        if (startDate && endDateRef.current) endDateRef.current.min = startDate;
    }, [startDate]);

    // Set max date for start date to today
    useEffect(() => {
        const today = getTodayDate();
        if (startDateRef.current) startDateRef.current.max = today;
        if (endDateRef.current) endDateRef.current.max = today;
    }, []);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`customer-list-modern ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {/* Header Section */}
            <div className="list-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faBoxesIcon} />
                    </div>
                    <div className="header-text">
                        <h1>{translations.fabProductList || 'Fabric Products List'}</h1>
                        <div className="shop-info">
                            <p>
                                {currentShop?.shop_name
                                    ? `${translations.shopName || 'Shop'}: ${currentShop.shop_name}`
                                    : `${translations.allFabProducts || 'My Fabric Products'} - ${user?.username}`}
                            </p>
                        </div>
                    </div>
                </div>
                {renderShopIndicator()}
            </div>

            {/* Advanced Filters Section */}
            <div className="advanced-filters-section">
                <div className="filters-row">
                    <div className="filter-group compact">
                        <select
                            value={filterBy}
                            onChange={(e) => setFilterBy(e.target.value)}
                            className="filter-select"
                            dir={isRTL ? 'rtl' : 'ltr'}
                        >
                            <option value="all">{translations.all || 'All'}</option>
                            <option value="batch">{translations.batchNumberFilter || 'Batch Number'}</option>
                            <option value="colorCode">{translations.colorCodeFilter || 'Color Code'}</option>
                            <option value="type">{translations.typeFilter || 'Type'}</option>
                            <option value="brand">{translations.brandFilter || 'Brand'}</option>
                            <option value="date">{translations.dateRange || 'Date Range'}</option>
                            <option value="added_by">{translations.addedByFilter || 'Added By'}</option>
                        </select>
                    </div>

                    {filterBy === 'batch' && (
                        <div className="filter-group compact">
                            <input type="text" value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)} placeholder={translations.batchNumberFilter || 'Batch number...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}
                    {filterBy === 'colorCode' && (
                        <div className="filter-group compact">
                            <input type="text" value={colorCodeFilter} onChange={(e) => setColorCodeFilter(e.target.value)} placeholder={translations.colorCodeFilter || 'Color code...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}
                    {filterBy === 'type' && (
                        <div className="filter-group compact">
                            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                                <option value="">{translations.selectType || 'Select Type'}</option>
                                {typeOptions.filter(opt => opt.value).map(opt => (
                                    <option key={opt.value} value={opt.value}>{translations[opt.value] || opt.label}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {filterBy === 'brand' && (
                        <div className="filter-group compact">
                            <input type="text" value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)} placeholder={translations.brandFilter || 'Brand...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}
                    {filterBy === 'added_by' && (
                        <div className="filter-group compact">
                            <input type="text" value={addedByFilter} onChange={(e) => setAddedByFilter(e.target.value)} placeholder={translations.addedByFilter || 'Added by username...'} className="filter-input compact" dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    )}
                    {filterBy === 'date' && (
                        <>
                            <div className="filter-group compact date-filter">
                                <input ref={startDateRef} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} max={getTodayDate()} className="filter-input compact date-input" title={translations.startDate || 'Start Date'} />
                            </div>
                            <div className="filter-group compact date-filter">
                                {/* <input ref={endDateRef} type="date */}
                                                                <input ref={endDateRef} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={startDate} max={getTodayDate()} className="filter-input compact date-input" title={translations.endDate || 'End Date'} />
                            </div>
                        </>
                    )}

                    <div className="filter-group compact">
                        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select" dir={isRTL ? 'rtl' : 'ltr'}>
                            <option value="all">{translations.stockStatus || 'Stock Status'}</option>
                            <option value="lowStock">{translations.lowStockFilter || 'Low Stock'}</option>
                            <option value="outOfStock">{translations.outOfStockFilter || 'Out of Stock'}</option>
                        </select>
                    </div>

                    <div className="filter-group compact search-group">
                        <div className="search-box compact">
                            <input type="text" placeholder={translations.searchPlaceholder || 'Search fabric products...'} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && applyFilters()} dir={isRTL ? 'rtl' : 'ltr'} />
                        </div>
                    </div>

                    <div className="filter-group compact">
                        <button onClick={fetchFabProducts} className="refresh-btn-modern compact" disabled={loading} title={translations.refresh || 'Refresh'}>
                            <FontAwesomeIcon icon={faRefresh} />
                            <span className="btn-text">{translations.refresh || 'Refresh'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner-modern"></div>
                    <p>{translations.loadingUserData || 'Loading your fabric product data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="no-shop-container">
                    <div className="no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view fabric products'}</p>
                        <button className="btn-modern btn-primary-modern" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table Section */}
                    <div className="table-scroll-container">
                        <div className="table-container-modern">
                            <div className="customer-table-wrapper">
                                <table className="customer-table-modern">
                                    <thead>
                                        <tr>
                                            <th className="id-column centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="name-column centered-cell">{translations.productName || 'Product'}</th>
                                            <th className="centered-cell">{translations.productColorCode || 'Color'}</th>
                                            <th className="centered-cell">{translations.batchNumber || 'Batch'}</th>
                                            <th className="centered-cell">{translations.type || 'Type'}</th>
                                            <th className="centered-cell">{translations.totalYards || 'Total Yards'}</th>
                                            <th className="centered-cell">{translations.totalMeters || 'Total Meters'}</th>
                                            <th className="centered-cell">{translations.totalRolls || 'Total Rolls'}</th>
                                            <th className="centered-cell">{translations.brand || 'Brand'}</th>
                                            <th className="centered-cell">{translations.unit || 'Unit'}</th>
                                            <th className="centered-cell">{translations.stockAlert || 'Alert'}</th>
                                            <th className="centered-cell">{translations.stockStatus || 'Status'}</th>
                                            <th className="centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="actions-column centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentProducts.map((product, index) => {
                                            const stockStatus = getStockStatus(product.total_yards, product.stock_alert);
                                            const addedBy = product.added_by_username || product.username || '-';
                                            const isSelfAdded = addedBy === user?.username;
                                            return (
                                                <tr key={product.id}>
                                                    <td className="id-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faHashtag} className="cell-icon" />
                                                            <span className="cell-text">{startIndex + index + 1}</span>
                                                        </div>
                                                    </td>
                                                    <td className="name-cell centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faBox} className="cell-icon" />
                                                            <span className="cell-text">{product.product_name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faPalette} className="cell-icon" />
                                                            <span className="cell-text">{product.product_colorcode || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faTag} className="cell-icon" />
                                                            <span className="cell-text">{product.batch_no}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="type-badge">{translations[product.type] || product.type || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faRulerCombined} className="cell-icon" />
                                                            <span className="cell-text">{formatNumber(product.total_yards)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faArrowsLeftRight} className="cell-icon" />
                                                            <span className="cell-text">{formatNumber(product.total_meters)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faCubes} className="cell-icon" />
                                                            <span className="cell-text">{product.total_rolls || '0'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faBuilding} className="cell-icon" />
                                                            <span className="cell-text">{product.brand || '-'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className="unit-badge">{product.unit === 'yards' ? (translations.unitYards || 'Yards') : (translations.unitMeters || 'Meters')}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <FontAwesomeIcon icon={faExclamationTriangle} className="cell-icon" />
                                                            <span className="cell-text">{formatNumber(product.stock_alert)}</span>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <div className="stock-status-badge" style={{ backgroundColor: stockStatus.color + '20', color: stockStatus.color, border: `1px solid ${stockStatus.color}40` }}>
                                                                {stockStatus.text}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="centered-cell">
                                                        <div className="cell-content centered-content">
                                                            <span className={`added-by-badge ${isSelfAdded ? 'self' : ''}`}>
                                                                <FontAwesomeIcon icon={faUser} className="cell-icon" size="xs" />
                                                                <span className="cell-text">{addedBy}</span>
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className="actions-cell-modern centered-cell">
                                                        <div className="cell-content centered-content actions-content">
                                                            <button onClick={() => handleEdit(product)} className="action-btn-modern edit-btn-modern" title={translations.edit || 'Edit'} disabled={product.username !== user?.username}>
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button onClick={() => handleDeleteClick(product.id)} className="action-btn-modern delete-btn-modern" title={translations.delete || 'Delete'} disabled={product.username !== user?.username}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* Pagination */}
                    {filteredProducts.length > 0 && currentShop && (
                        <div className="pagination-modern">
                            <div className="pagination-info">
                                <span>{translations.showing || 'Showing'} {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} {translations.of || 'of'} {filteredProducts.length} {translations.products || 'products'}</span>
                                {currentShop?.shop_name && (<span className="shop-info-pagination"> | {translations.shopName || 'Shop'}: {currentShop.shop_name}</span>)}
                                <span style={{ marginLeft: '20px', color: '#059669', fontWeight: '600' }}>{translations.totalYardsValue || 'Total Yards'}: {formatNumber(totalYards)} yds</span>
                            </div>
                            <div className="pagination-controls">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="pagination-btn">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>
                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) pageNum = i + 1;
                                        else if (currentPage <= 3) pageNum = i + 1;
                                        else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                        else pageNum = currentPage - 2 + i;
                                        return (<button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`page-number ${currentPage === pageNum ? 'active' : ''}`}>{pageNum}</button>);
                                    })}
                                </div>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-btn">
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedProduct && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div className="modal-icon"><FontAwesomeIcon icon={faBox} /></div>
                            <div className="modal-title">
                                <h3>{translations.fabProductDetails || 'Fabric Product Details'}</h3>
                                <span className="fab-product-number-badge">#{selectedProduct.id}</span>
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowViewModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="modal-body-modern view-modal-body">
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faBox} />Product Summary</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.productId || 'Product ID'}:</span><span className="view-value">#{selectedProduct.id}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.productName || 'Product Name'}:</span><span className="view-value">{selectedProduct.product_name}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.productColorCode || 'Color Code'}:</span><span className="view-value">{selectedProduct.product_colorcode || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.batchNumber || 'Batch Number'}:</span><span className="view-value">{selectedProduct.batch_no}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.type || 'Type'}:</span><span className="view-value">{translations[selectedProduct.type] || selectedProduct.type || '-'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.brand || 'Brand'}:</span><span className="view-value">{selectedProduct.brand || '-'}</span></div>
                                </div>
                            </div>
                            <div className="view-section">
                                <h4 className="view-section-title"><FontAwesomeIcon icon={faWarehouse} />Stock Information</h4>
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.unit || 'Unit'}:</span><span className="view-value">{selectedProduct.unit === 'yards' ? (translations.unitYards || 'Yards') : (translations.unitMeters || 'Meters')}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.stockQuantity || 'Stock Quantity'}:</span><span className="view-value">{formatNumber(selectedProduct.unit === 'yards' ? selectedProduct.total_yards : selectedProduct.total_meters)}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.totalYards || 'Total Yards'}:</span><span className="view-value">{formatNumber(selectedProduct.total_yards)} yds</span></div>
                                    <div className="view-row"><span className="view-label">{translations.totalMeters || 'Total Meters'}:</span><span className="view-value">{formatNumber(selectedProduct.total_meters)} m</span></div>
                                    <div className="view-row"><span className="view-label">{translations.totalRolls || 'Total Rolls'}:</span><span className="view-value">{selectedProduct.total_rolls || '0'}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.stockAlert || 'Low Stock Alert'}:</span><span className="view-value">{formatNumber(selectedProduct.stock_alert)}</span></div>
                                    <div className="view-row"><span className="view-label">{translations.stockStatus || 'Stock Status'}:</span><span className="view-value"><div className="stock-status-badge" style={{ backgroundColor: getStockStatus(selectedProduct.total_yards, selectedProduct.stock_alert).color + '20', color: getStockStatus(selectedProduct.total_yards, selectedProduct.stock_alert).color, border: `1px solid ${getStockStatus(selectedProduct.total_yards, selectedProduct.stock_alert).color}40` }}>{getStockStatus(selectedProduct.total_yards, selectedProduct.stock_alert).text}</div></span></div>
                                </div>
                            </div>
                            <div className="view-section metadata-section">
                                <div className="view-grid">
                                    <div className="view-row"><span className="view-label">{translations.addedBy || 'Added By'}:</span><span className="view-value"><span className="added-by-badge">{selectedProduct.added_by_username || selectedProduct.username || '-'}</span></span></div>
                                    <div className="view-row"><span className="view-label">{translations.date || 'Date Added'}:</span><span className="view-value">{formatDate(selectedProduct.date)}</span></div>
                                    {selectedProduct.shop_name && (<div className="view-row"><span className="view-label">{translations.shopInfo || 'Shop'}:</span><span className="view-value">{selectedProduct.shop_name} {selectedProduct.shop_code ? `(${selectedProduct.shop_code})` : ''}</span></div>)}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-modern btn-secondary-modern" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            {selectedProduct.username === user?.username && (
                                <button className="btn-modern btn-primary-modern" onClick={() => { setShowViewModal(false); handleEdit(selectedProduct); }}>
                                    <FontAwesomeIcon icon={faEdit} />{translations.edit || 'Edit'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal - MOVABLE, RESIZABLE */}
            {showEditModal && selectedProduct && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div ref={modalRef} className="modal-content-modern edit-modal-content" onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', left: modalPosition.x || '50%', top: modalPosition.y || '50%', transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none', width: modalSize.width, minWidth: '450px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {/* Resize Handles */}
                        <div className="resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        <div className="modal-header-modern" onMouseDown={startDrag} style={{ cursor: 'move', flexShrink: 0 }}>
                            <div className="modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="modal-title">
                                <h3>{translations.editFabProduct || 'Edit Fabric Product'}</h3>
                                <span className="fab-product-number-badge">#{selectedProduct.id}</span>
                            </div>
                            <div className="width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button key={key} className={`width-btn ${currentWidthMode === key ? 'active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label} type="button">
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            <button className="close-btn-modern" onClick={() => setShowEditModal(false)} type="button"><FontAwesomeIcon icon={faTimes} /></button>
                        </div>

                        <form onSubmit={handleUpdateProduct} style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <div className="modal-body-modern" style={{ flex: 1, overflow: 'auto' }}>
                                <div className="form-grid edit-form-grid">
                                    <div className={`form-group ${editFormErrors.product_name ? 'has-error' : ''}`}>
                                        <label htmlFor="product_name">{translations.productName || 'Product Name'} <span className="required">*</span></label>
                                        <input type="text" id="product_name" name="product_name" value={editFormData.product_name} onChange={handleEditFormChange} required disabled={editLoading} dir={isRTL ? 'rtl' : 'ltr'} />
                                        {editFormErrors.product_name && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.product_name}</div>)}
                                    </div>

                                    <div className={`form-group ${editFormErrors.product_colorcode ? 'has-error' : ''}`}>
                                        <label htmlFor="product_colorcode">{translations.productColorCode || 'Color Code'}</label>
                                        <input type="text" id="product_colorcode" name="product_colorcode" value={editFormData.product_colorcode} onChange={handleEditFormChange} disabled={editLoading} dir={isRTL ? 'rtl' : 'ltr'} />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="batch_no">{translations.batchNumber || 'Batch Number'}</label>
                                        <input type="text" id="batch_no" name="batch_no" value={editFormData.batch_no} readOnly disabled className="readonly-input" dir={isRTL ? 'rtl' : 'ltr'} />
                                        <small className="field-hint">Batch number is auto-generated and cannot be edited</small>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="type">{translations.type || 'Type'}</label>
                                        <select id="type" name="type" value={editFormData.type} onChange={handleEditFormChange} disabled={editLoading} dir={isRTL ? 'rtl' : 'ltr'}>
                                            {typeOptions.map(opt => (<option key={opt.value} value={opt.value}>{translations[opt.value] || opt.label}</option>))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="brand">{translations.brand || 'Brand'}</label>
                                        <select id="brand" name="brand" value={editFormData.brand} onChange={handleEditFormChange} disabled={editLoading} dir={isRTL ? 'rtl' : 'ltr'}>
                                            <option value="">Select Brand</option>
                                            {brandOptions.map(opt => (<option key={opt.value} value={opt.label}>{opt.label}</option>))}
                                        </select>
                                    </div>

                                    <div className="form-group full-width">
                                        <label className="unit-toggle-label">{translations.selectUnit || 'Select Stock Unit'}</label>
                                        <div className="unit-toggle-container">
                                            <button
                                                type="button"
                                                className={`unit-toggle-btn ${editFormData.unit === 'yards' ? 'active' : ''}`}
                                                onClick={() => setEditFormData(prev => ({ ...prev, unit: 'yards' }))}
                                            >
                                                <FontAwesomeIcon icon={faRulerCombined} />
                                                <span>{translations.unitYards || 'Yards'}</span>
                                            </button>
                                            <button
                                                type="button"
                                                className={`unit-toggle-btn ${editFormData.unit === 'meters' ? 'active' : ''}`}
                                                onClick={() => setEditFormData(prev => ({ ...prev, unit: 'meters' }))}
                                            >
                                                <FontAwesomeIcon icon={faArrowsLeftRight} />
                                                <span>{translations.unitMeters || 'Meters'}</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className={`form-group ${editFormErrors.stock_quantity ? 'has-error' : ''}`}>
                                        <label htmlFor="stock_quantity">{translations.stockQuantity || 'Stock Quantity'} <span className="required">*</span></label>
                                        <input type="number" id="stock_quantity" name="stock_quantity" value={editFormData.stock_quantity} onChange={handleEditFormChange} required step="0.01" min="0" disabled={editLoading} dir="ltr" />
                                        {editFormErrors.stock_quantity && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.stock_quantity}</div>)}
                                        <small className="field-hint">{translations.stockInfo}</small>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="total_rolls">{translations.totalRolls || 'Total Rolls'}</label>
                                        <input type="number" id="total_rolls" name="total_rolls" value={editFormData.total_rolls} onChange={handleEditFormChange} step="1" min="0" disabled={editLoading} dir="ltr" />
                                        <small className="field-hint">{translations.totalRollsHint}</small>
                                    </div>

                                    <div className={`form-group ${editFormErrors.stock_alert ? 'has-error' : ''}`}>
                                        <label htmlFor="stock_alert">{translations.stockAlert || 'Low Stock Alert'} <span className="required">*</span></label>
                                        <input type="number" id="stock_alert" name="stock_alert" value={editFormData.stock_alert} onChange={handleEditFormChange} required step="1" min="0" disabled={editLoading} dir="ltr" />
                                        {editFormErrors.stock_alert && (<div className="error-message"><FontAwesomeIcon icon={faExclamationTriangle} />{editFormErrors.stock_alert}</div>)}
                                    </div>
                                </div>

                                <div className="edit-product-info">
                                    <p><strong>{translations.productId || 'Product ID'}:</strong> #{selectedProduct.id}</p>
                                    <p><strong>{translations.batchNumber || 'Batch'}:</strong> {selectedProduct.batch_no}</p>
                                    <p><strong>{translations.addedBy || 'Added By'}:</strong> {selectedProduct.added_by_username || selectedProduct.username}</p>
                                    <p><strong>{translations.shopName || 'Shop'}:</strong> {currentShop?.shop_name || 'N/A'}</p>
                                </div>
                            </div>

                            <div className="modal-footer-modern" style={{ flexShrink: 0 }}>
                                <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowEditModal(false)} disabled={editLoading}>
                                    <FontAwesomeIcon icon={faTimes} />{translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="btn-modern btn-primary-modern" disabled={editLoading}>
                                    {editLoading ? (<><div className="btn-spinner"></div><span>{translations.updating || 'Updating...'}</span></>) : (<><FontAwesomeIcon icon={faSave} /><span>{translations.saveChanges || 'Save Changes'}</span></>)}
                                </button>
                            </div>
                        </form>

                        {editLoading && (<div className="loading-overlay edit-loading-overlay"><div className="loading-content"><div className="loading-spinner"></div><p>{translations.updating || 'Updating...'}</p></div></div>)}
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-modern delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern delete-header">
                            <div className="modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="modal-title"><h3 style={{ color: 'var(--customer-error-color)' }}>{translations.confirmDelete || 'Confirm Delete'}</h3></div>
                            <button className="close-btn-modern" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="delete-message">
                                <p style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--customer-text-primary)' }}>{translations.deleteMessage || 'Are you sure you want to delete this fabric product?'}</p>
                                <p style={{ fontSize: '0.9rem', color: 'var(--customer-warning-color)' }}><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p>
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button type="button" className="btn-modern btn-secondary-modern" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{translations.noCancel || 'No, Cancel'}</button>
                            <button type="button" className="btn-modern btn-delete-modern" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{translations.yesDelete || 'Yes, Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FabProductsList;